import React, { useState } from 'react';
import FontIcon from './common/FontIcon';
import logger from '../utils/logger';
import { PREFERRED_ICON_TYPE } from '../config/TypeConfig';

/**
 * 通用应用图标组件，处理不同类型的图标显示(图片/文字)和背景色
 * 
 * @param {Object} props 组件属性
 * @param {string} [props.ico] - 图片图标URL
 * @param {string} [props.iconUrl] - 备选图片图标URL(当ico不存在时使用)
 * @param {string} [props.textIcon] - 文字图标内容
 * @param {string} [props.bgColor] - 背景颜色/渐变
 * @param {number} [props.preferredIconType] - 优先显示的图标类型(PREFERRED_ICON_TYPE.ICO代表ico, PREFERRED_ICON_TYPE.TEXT代表text)
 * @param {string} [props.iconColor] - 默认图标颜色(当使用默认图标时)
 * @param {Function} [props.onImageError] - 图片加载失败处理函数
 * @param {Object} [props.wrapperStyle] - 包装器额外样式
 * @param {Object} [props.imageStyle] - 图片额外样式
 * @param {Object} [props.textStyle] - 文字额外样式
 * @param {Object} [props.iconStyle] - 默认图标额外样式
 * @param {string} [props.className] - 额外CSS类名
 * @param {number} [props.size] - 图标尺寸(默认40px)
 */
const AppIcon = ({
  ico,
  iconUrl,
  textIcon,
  bgColor = 'white',
  preferredIconType,
  iconColor = 'white',
  onImageError,
  wrapperStyle = {},
  imageStyle = {},
  textStyle = {},
  iconStyle = {},
  className = '',
  size = 40
}) => {
  // 添加状态来跟踪图片加载失败
  const [imageLoadFailed, setImageLoadFailed] = useState(false);
  
  // 确定使用的图标类型
  const hasIco = ico || iconUrl;
  const hasTextIcon = textIcon && textIcon.trim() !== '';
  
  // 根据优先级确定显示哪种图标，考虑图片加载失败的情况
  let useIco = hasIco && !imageLoadFailed; // 图片加载失败时不使用图片
  let useTextIcon = hasTextIcon;
  
  if (preferredIconType === PREFERRED_ICON_TYPE.ICO && hasIco && !imageLoadFailed) {
    useIco = true;
    useTextIcon = false;
  } else if (preferredIconType === PREFERRED_ICON_TYPE.TEXT && hasTextIcon) {
    useIco = false;
    useTextIcon = true;
  } else if (hasIco && !imageLoadFailed) {
    useIco = true;
    useTextIcon = false;
  } else if (hasTextIcon) {
    useIco = false;
    useTextIcon = true;
  }
  
  // 图片加载失败时，如果有文字图标则使用文字图标
  if (imageLoadFailed && hasTextIcon) {
    useIco = false;
    useTextIcon = true;
  }
  
  // 处理背景样式
  const isTransparent = bgColor === 'transparent';
  const validBgColor = bgColor || 'white'; // 确保bgColor始终有有效值
  
  const wrapperBaseStyle = {
    width: `${size}px`,
    height: `${size}px`,
    borderRadius: Math.floor(size / 5) + 'px', // 确保圆角与尺寸成比例
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    overflow: 'hidden',
    // 处理透明或渐变背景
    ...(isTransparent ? {
      background: 'linear-gradient(45deg, #eee 25%, transparent 25%, transparent 75%, #eee 75%, #eee), linear-gradient(45deg, #eee 25%, transparent 25%, transparent 75%, #eee 75%, #eee)',
      backgroundSize: '16px 16px',
      backgroundPosition: '0 0, 8px 8px',
    } : typeof validBgColor === 'string' && validBgColor.includes('gradient') ? { background: validBgColor } : { backgroundColor: validBgColor })
  };
  
  // 处理图片加载失败
  const handleImageError = (e) => {
    logger.log('[AppIcon] 图标加载失败，切换到文字图标:', ico || iconUrl);
    setImageLoadFailed(true);
    if (onImageError) onImageError(e);
  };
  
  // 渲染图标内容
  const renderIconContent = () => {
    if (useIco) {
      return (
        <img 
          src={ico || iconUrl} 
          alt="icon" 
          draggable={false}
          onError={handleImageError}
          style={{
            width: `${size}px`,
            height: `${size}px`,
            objectFit: 'contain',
            filter: isTransparent ? 'drop-shadow(0 0 1px rgba(0,0,0,0.2))' : undefined,
            ...imageStyle
          }}
        />
      );
    } else if (useTextIcon) {
      // 文字图标处理：最多显示2行，每行最多2个汉字
      const formatTextIcon = (text) => {
        if (!text) return '';
        
        // 移除空格
        text = text.trim();
        
        // 确保不超过4个字符（2行x2字/行）
        if (text.length > 4) {
          text = text.substring(0, 4);
        }
        
        // 如果是3个字母，保持一行显示
        if (text.length === 3 && /^[a-zA-Z]+$/.test(text)) {
          return text;
        }
        
        // 分割成两行（超过2个字符且不是3个字母的情况）
        if (text.length > 2) {
          const firstLine = text.substring(0, 2);
          const secondLine = text.substring(2, 4);
          return (
            <>
              <span>{firstLine}</span>
              <span>{secondLine}</span>
            </>
          );
        }
        
        // 只有一行
        return text;
      };
      
      const formattedText = formatTextIcon(textIcon);
      
      // 根据背景颜色判断是否需要添加文字阴影
      const isLightBackground = bgColor === 'white' || bgColor === 'transparent';
      const textColor = isLightBackground ? '#333' : 'white'; // 浅色背景使用深色文字
      
      return (
        <div 
          style={{
            color: textColor, 
            fontWeight: 'bold',
            fontSize: Math.max(Math.floor(size / 3), 14) + 'px', // 根据尺寸调整字体大小
            textAlign: 'center',
            lineHeight: '1.2',
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'center',
            alignItems: 'center',
            width: `${size}px`,
            height: `${size}px`,
            textShadow: isLightBackground 
              ? '0 1px 2px rgba(0,0,0,0.3)' // 浅色背景时添加深色阴影
              : '0 1px 2px rgba(0,0,0,0.5), 0 0 5px rgba(0,0,0,0.3)', // 深色背景时添加更强的阴影
            ...textStyle
          }}
        >
          {formattedText}
        </div>
      );
    } else {
      // 默认使用globe图标
      return (
        <FontIcon name="globe" className="default-icon" />
      );
    }
  };
  
  // 组合最终样式
  const finalWrapperStyle = { ...wrapperBaseStyle, ...wrapperStyle };
  const wrapperClassName = `app-icon-wrapper ${isTransparent ? 'transparent' : ''} ${className}`.trim();
  
  return (
    <div className={wrapperClassName} style={finalWrapperStyle}>
      {renderIconContent()}
    </div>
  );
};

export default AppIcon; 